import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import QuizContent from './shared/QuizContent';
import QuizTitle from './shared/QuizTitle';
import LazyImage from './shared/LazyImage';
import PlayPauseIcon from './shared/PlayPauseIcon';
import {
  CDN_BASE_URL,
  CDN_URL,
  STORAGE_KEYS,
  QUIZ_OPTIONS_DATA,
  CHARACTER_BY_ETHNICITY,
} from '../constants';
import { persistPostSignupFlow } from '../utils/postSignupRedirect';
import { trackStep } from '../utils/analytics';
import {
  ethnicityKey,
  useGrowthBookValues,
} from '../contexts/GrowthBookContext';

import stepSisSvg from '../assets/StepSis.svg';
import stepMomSvg from '../assets/StepMom.svg';
import girlfriendSvg from '../assets/Girlfriend.svg';
import chatIcon from '../assets/Icon_chat.svg';
import giftIcon from '../assets/gift.svg';
import heartBgSvg from '../assets/heart_bg.svg';
import heartSvg from '../assets/heart.svg';
import LogoSvg from '../assets/Logo.svg';

const RELATIONSHIP_ICONS = {
  stepSis: stepSisSvg,
  stepMom: stepMomSvg,
  girlfriend: girlfriendSvg,
};

// Placeholder character images served from the existing CDN. These need to be
// swapped for real Sora Tanaka / Isabelle Davies hero images once assets land.
const CHARACTER_IMAGES = {
  sora_tanaka: `${CDN_URL}/characters/sora_tanaka/hero.png`,
  isabelle_davies: `${CDN_URL}/characters/isabelle_davies/hero.png`,
};

const FALLBACK_CHARACTER_IMAGE = `${CDN_BASE_URL}/Realistic/step1-image2.png`;

const GENERATION_MS = 3000;

const Summary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const { navType, isReady: gbReady, videos: gbVideos } = useGrowthBookValues();

  const [selections, setSelections] = useState(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [characterImageError, setCharacterImageError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    // Summary is the 6th and final quiz screen.
    trackStep(6);

    try {
      const raw = localStorage.getItem(STORAGE_KEYS.AI_TRANS_SELECTIONS);
      setSelections(raw ? JSON.parse(raw) : {});
    } catch {
      setSelections({});
    }

    const timer = setTimeout(() => setIsGenerating(false), GENERATION_MS);
    return () => clearTimeout(timer);
  }, []);

  const ethnicityValue = selections?.step1?.ethnicity;
  const character =
    CHARACTER_BY_ETHNICITY[ethnicityValue] || CHARACTER_BY_ETHNICITY.Asian;
  const heroVideoUrl =
    gbVideos?.generateVideo?.[ethnicityValue] ||
    gbVideos?.generateVideo?.Asian ||
    '';
  // Step 2/3/4 videos all share the Step 1 ethnicity branch — derive it once
  // and reuse for every attribute card below.
  const eKey = ethnicityKey(selections?.step1?.ethnicity);

  const lookupOption = useCallback((collection, value) => {
    return QUIZ_OPTIONS_DATA[collection]?.find((o) => o.value === value);
  }, []);

  const ethnicity = useMemo(
    () => lookupOption('ethnicity', selections?.step1?.ethnicity),
    [selections, lookupOption],
  );
  const personality = useMemo(
    () => lookupOption('personality', selections?.step2?.personality),
    [selections, lookupOption],
  );
  const bodyTypeOption = useMemo(
    () => lookupOption('bodyType', selections?.step3?.bodyType),
    [selections, lookupOption],
  );
  const hairStyle = useMemo(
    () => lookupOption('hairStyle', selections?.step4?.hairStyle),
    [selections, lookupOption],
  );
  const eyeColor = useMemo(
    () => lookupOption('eyeColor', selections?.step4?.eyeColor),
    [selections, lookupOption],
  );
  const voice = useMemo(
    () => lookupOption('voice', selections?.step5?.voice),
    [selections, lookupOption],
  );
  const relationship = useMemo(
    () => lookupOption('relationship', selections?.step5?.relationship),
    [selections, lookupOption],
  );

  // Render a video if a GB-resolved URL is present, otherwise fall back to
  // the option's static image. Used for the Summary attribute cards that
  // mirror what played on the quiz steps (ethnicity / personality / body).
  const renderMedia = (videoUrl, imageUrl, alt) => {
    if (videoUrl && videoUrl.trim()) {
      return (
        <video
          src={videoUrl}
          poster={imageUrl}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          controls={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
      );
    }
    return <LazyImage src={imageUrl} alt={alt} />;
  };

  const handleStartChat = useCallback(() => {
    // Persist the GB-resolved post-signup flow now so the redirect (which
    // runs after page reload triggered by signup completion) can read it
    // synchronously without waiting on GrowthBook. Guarded by gbReady so the
    // pre-resolve default 'PAYWALL' doesn't get persisted when the real flag
    // would have been 'CHAT' (GBProvider's 1.5s safety timeout flips isReady
    // even when GB is blocked, so user is never stuck here).
    if (gbReady) {
      persistPostSignupFlow(navType);
    }
    navigate('/signup');
  }, [navigate, navType, gbReady]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      audioRef.current.onended = () => setIsPlaying(false);
    }
  }, [isPlaying]);

  if (!selections) {
    return (
      <div className="quizContentWrapper lastStep">
        <QuizContent>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>{t('loading')}</p>
          </div>
        </QuizContent>
      </div>
    );
  }

  const characterImageSrc = characterImageError
    ? FALLBACK_CHARACTER_IMAGE
    : CHARACTER_IMAGES[character.slug];

  return (
    <div className="quizContentWrapper lastStep">
      <img
        src={LogoSvg}
        alt="Swipey"
        className="app-logo"
        width={100}
        height={24}
      />
      <div className="progress-stepper-text step5-ribbon">
        <img src={giftIcon} alt="" width={12} height={12} />
        {t('limited_offer')}{' '}
        <span className="highlight-text">{t('offer_price')}</span>
        <img src={giftIcon} alt="" width={12} height={12} />
      </div>

      <QuizContent className="step5-design">
        {!isGenerating && <QuizTitle>{t('fantasy_babe')}</QuizTitle>}

        <div className="result-grid">
          {/* Left — character reveal (or generating animation) */}
          <div className="result-main">
            {isGenerating ? (
              <div className="result-generating">
                {heroVideoUrl ? (
                  <video
                    src={heroVideoUrl}
                    className="result-generating-blur"
                    muted
                    playsInline
                    preload="auto"
                    controls={false}
                    aria-hidden="true"
                  />
                ) : (
                  <img
                    src={characterImageSrc}
                    alt=""
                    className="result-generating-blur"
                    onError={() => setCharacterImageError(true)}
                    aria-hidden="true"
                  />
                )}
                <div className="result-generating-overlay" aria-hidden="true" />
                <img
                  src={heartBgSvg}
                  alt=""
                  className="result-generating-heart-bg"
                  aria-hidden="true"
                />
                <img
                  src={heartSvg}
                  alt=""
                  className="result-generating-heart"
                  aria-hidden="true"
                />
                <div className="result-generating-label">
                  {t('generating')}
                </div>
              </div>
            ) : (
              <>
                {heroVideoUrl ? (
                  <video
                    src={heroVideoUrl}
                    autoPlay
                    muted
                    playsInline
                    loop
                    preload="auto"
                    controls={false}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <img
                    src={characterImageSrc}
                    alt={character.displayName}
                    onError={() => setCharacterImageError(true)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )}
                <div className="result-main-label">
                  {character.displayName}
                </div>
              </>
            )}
          </div>

          {/* Right — attributes grid */}
          <div className="result-cards">
            {ethnicity && (
              <div className="result-card result-card-ethnicity">
                {renderMedia(
                  gbVideos?.step1?.[ethnicity.value],
                  ethnicity.image,
                  ethnicity.alt,
                )}
                <span>{t(ethnicity.text)}</span>
              </div>
            )}
            <div className="result-card result-card-age">
              <div className="age-number">
                {selections?.step5?.age || 22}
              </div>
              <span>{t('years')}</span>
            </div>

            {bodyTypeOption && (
              <div className="result-card">
                {renderMedia(
                  gbVideos?.step3?.[eKey]?.[bodyTypeOption.value],
                  bodyTypeOption.image,
                  bodyTypeOption.alt,
                )}
                <span>{t(bodyTypeOption.text)}</span>
              </div>
            )}
            {hairStyle && (
              <div className="result-card">
                {renderMedia(
                  gbVideos?.step4?.[eKey]?.haircut?.[hairStyle.value],
                  hairStyle.image,
                  hairStyle.alt,
                )}
                <span>{t(hairStyle.text)}</span>
              </div>
            )}

            {personality && (
              <div className="result-card">
                {renderMedia(
                  gbVideos?.step2?.[eKey]?.[personality.value],
                  personality.image,
                  personality.alt,
                )}
                <span>{t(personality.text)}</span>
              </div>
            )}
            {eyeColor && (
              <div className="result-card">
                {(() => {
                  const eyeVideo = gbVideos?.step4?.[eKey]?.eye?.[
                    eyeColor.value
                  ];
                  // Mirror Step 4: eyes are a static first-frame poster, not
                  // a looping video. `#t=0.001` makes Safari/iOS render the
                  // frame even though we never call play().
                  if (eyeVideo && eyeVideo.trim()) {
                    return (
                      <video
                        src={`${eyeVideo}#t=0.001`}
                        poster={eyeColor.image}
                        muted
                        playsInline
                        preload="auto"
                        controls={false}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                        }}
                      />
                    );
                  }
                  return <LazyImage src={eyeColor.image} alt={eyeColor.alt} />;
                })()}
                <span>{t(eyeColor.text)}</span>
              </div>
            )}

            {relationship && (
              <div className="result-card result-card-personality">
                <div className="personality-icon-wrapper">
                  <div className="icon-badge">
                    <img
                      src={RELATIONSHIP_ICONS[relationship.value]}
                      alt={relationship.value}
                    />
                  </div>
                </div>
                <span>{t(relationship.text)}</span>
              </div>
            )}
            {voice && (
              <div
                className="result-card result-card-voice"
                onClick={togglePlay}
              >
                <div className="voice-icon-wrapper">
                  <PlayPauseIcon isPlaying={isPlaying} />
                </div>
                <span>{t(voice.text)}</span>
                {voice.audio && (
                  <audio
                    ref={audioRef}
                    src={voice.audio}
                    preload="metadata"
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="cta-container">
          <button
            className="bring-to-life-button"
            onClick={handleStartChat}
            disabled={isGenerating}
          >
            <img
              src={chatIcon}
              alt=""
              className="sparkle-icon"
              width={20}
              height={20}
            />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>
              {t('start_chat')}
            </span>
          </button>
        </div>
      </QuizContent>
    </div>
  );
};

export default Summary;
